import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { User } from 'types/interfaces/user/User';

import { getPhotoPlaceholders } from 'helpers/photoPlaceholders';
import {
  getSessionStorageItem,
  setSessionStorageItem,
} from 'helpers/sessionStorage';
import { setHasMatch } from 'store/common/commonSlice';

import { useToast } from './useToast';

export const MATCHES_NOTIFICATIONS_QUEUE = 'matches-queue';
export const MATCHES_NOTIFICATIONS_STORAGE_KEY = 'MATCHES_NOTIFICATIONS';

enum MatchNotificationTypes {
  Popup,
  Toast,
}
const MAX_MATCHES_POPUP_COUNT = 5;
const MAX_TOASTS_COUNT = 20;
export const MAX_TOTAL_NOTIFICATIONS_COUNT =
  MAX_MATCHES_POPUP_COUNT + MAX_TOASTS_COUNT;

interface MatchNotification {
  contact: User;
  type: MatchNotificationTypes;
  created: Date;
  isShown: boolean;
}

export const useMatchesNotifications = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { showMessageToast } = useToast();

  const createNotificationQueueItem = (payload: {
    contact: User;
    type: MatchNotificationTypes;
  }) => {
    return {
      contact: payload.contact,
      type: payload.type,
      created: new Date(),
      isShown: false,
    };
  };

  const getMatchNotificationsQueue = () => {
    const storedMatchesQueue = getSessionStorageItem(
      MATCHES_NOTIFICATIONS_QUEUE
    );
    return (
      storedMatchesQueue ? JSON.parse(storedMatchesQueue) : []
    ) as MatchNotification[];
  };

  const setMatchNotificationsQueue = (notifications: MatchNotification[]) => {
    setSessionStorageItem(
      MATCHES_NOTIFICATIONS_QUEUE,
      JSON.stringify(notifications)
    );
  };

  const getMatchPopupNotificationsQueue = (
    notifications: MatchNotification[]
  ) => {
    return notifications.slice(0, MAX_MATCHES_POPUP_COUNT);
  };

  const addMatchNotificationToQueue = useCallback(
    (notification: MatchNotification) => {
      const matchesQueue = getMatchNotificationsQueue();

      setMatchNotificationsQueue([...matchesQueue, notification]);
    },
    []
  );

  const updateMatchNotificationInQueue = useCallback(
    (notification: MatchNotification) => {
      const matchesNotificationsQueue = getMatchNotificationsQueue();
      const updatedMatchNotifications = matchesNotificationsQueue.map(
        (queueNotification) => {
          if (queueNotification.contact.id === notification.contact.id) {
            return notification;
          }
          return queueNotification;
        }
      );
      setMatchNotificationsQueue(updatedMatchNotifications);
    },
    []
  );

  const showMatchNotification = useCallback(
    (notification: MatchNotification) => {
      if (notification.type === MatchNotificationTypes.Popup) {
        dispatch(setHasMatch(notification.contact));
      } else {
        showMessageToast({
          content: {
            name: notification.contact.username,
            message: 'It’s a Perfect Match',
            photo:
              notification.contact.photo_url ??
              getPhotoPlaceholders(notification.contact.gender, 1)[0],
            onClick: () => history.push(`/threads/${notification.contact.id}`),
          },
        });
      }

      updateMatchNotificationInQueue({
        ...notification,
        isShown: true,
      });
    },
    [dispatch, history, showMessageToast, updateMatchNotificationInQueue]
  );

  const matchNotificationHandler = useCallback(
    (contact: User) => {
      const matchNotificationsQueue = getMatchNotificationsQueue();
      const matchPopupNotificationsQueue = getMatchPopupNotificationsQueue(
        matchNotificationsQueue
      );

      const notification = createNotificationQueueItem({
        contact,
        type:
          matchPopupNotificationsQueue.length < MAX_MATCHES_POPUP_COUNT
            ? MatchNotificationTypes.Popup
            : MatchNotificationTypes.Toast,
      });

      addMatchNotificationToQueue(notification);

      showMatchNotification(notification);
    },
    [addMatchNotificationToQueue, showMatchNotification]
  );

  return {
    matchNotificationHandler,
  };
};
